import type {
  CredentialCreationOptionsJSON,
  CredentialRequestOptionsJSON
} from "@github/webauthn-json";
import {
  type AuthenticationPublicKeyCredential,
  create,
  get,
  parseCreationOptionsFromJSON,
  parseRequestOptionsFromJSON,
  type RegistrationPublicKeyCredential
} from "@github/webauthn-json/browser-ponyfill";

export interface PasskeySupport {
  available: boolean;
  secureContext: boolean;
  credentialsApi: boolean;
  publicKeyCredential: boolean;
  platformAuthenticator: boolean | null;
  reason?: string;
}

export function getPasskeyErrorMessage(error: unknown) {
  const name = (error as { name?: string } | null)?.name;
  if (name === "NotAllowedError" || name === "AbortError") return "Passkey sign-in was cancelled or timed out.";
  if (name === "SecurityError") return "Passkeys require HTTPS or localhost.";
  if (name === "NotSupportedError") return "This browser or device does not support passkeys.";
  if (name === "InvalidStateError") return "A passkey already exists for this account on this device.";
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string") return error;
  return "Passkey authentication failed.";
}

export async function getPasskeySupport(): Promise<PasskeySupport> {
  const publicKeyCredential = window.PublicKeyCredential as
    | (typeof PublicKeyCredential & { isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean> })
    | undefined;
  const secureContext = window.isSecureContext !== false;
  const credentialsApi = Boolean(window.navigator?.credentials);
  let platformAuthenticator: boolean | null = null;

  if (publicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
    try {
      platformAuthenticator = await publicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      platformAuthenticator = null;
    }
  }

  const available = secureContext && credentialsApi && Boolean(publicKeyCredential);
  return {
    available,
    secureContext,
    credentialsApi,
    publicKeyCredential: Boolean(publicKeyCredential),
    platformAuthenticator,
    reason: available
      ? undefined
      : !secureContext
        ? "Passkeys require HTTPS or localhost."
        : !credentialsApi
          ? "This browser does not expose the Credentials API."
          : "This browser does not support WebAuthn passkeys."
  };
}

async function assertPasskeySupport() {
  const support = await getPasskeySupport();
  if (!support.available) {
    throw new Error(support.reason || "Passkeys are not available.");
  }
  return support;
}

export async function createPasskeyAttestation(options: CredentialCreationOptionsJSON): Promise<RegistrationPublicKeyCredential> {
  await assertPasskeySupport();
  return create(parseCreationOptionsFromJSON(options));
}

export async function getPasskeyAssertion(options: CredentialRequestOptionsJSON): Promise<AuthenticationPublicKeyCredential> {
  await assertPasskeySupport();
  return get(parseRequestOptionsFromJSON(options));
}
